import { Button, Chip, Typography } from '@mui/material';
import { Stack } from '@mui/system';
import { showToast } from '@tix-ticketing/ui-components';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import type { OrderResponse } from 'api/generated/types.gen';
import { DiscountCodeDialog } from 'components/cart/discount-code-dialog';

/**
 * Discount code entry in the cart's order summary, offered only when the order
 * allows one. The applied code is read off the order; its effect shows in the
 * repriced ticket lines, as the order carries no separate discount amount. An
 * applied code can't be removed.
 */
export const CartDiscountCode = ({
  order
}: {
  order?: OrderResponse;
}) => {
  const intl = useIntl();
  const [dialogOpen, setDialogOpen] = useState(false);
  const applied = order?.appliedDiscountCode;
  const appliedName = applied?.label || applied?.code;
  if (appliedName) {
    return <Stack direction="row" spacing={1} sx={{
      alignItems: 'center',
      width: '100%'
    }}>
        <Typography variant="body2" sx={{
        color: 'text.secondary'
      }}>
          <FormattedMessage id="cart.discountCode.label" defaultMessage={[{
          "type": 0,
          "value": "Discount code"
        }]} />
        </Typography>
        <Chip data-testid="cart-applied-discount-code" variant="outlined" label={appliedName} sx={{
        borderColor: 'monochrome.outlineRestingBorder',
        color: 'monochrome.contrastText'
      }} />
      </Stack>;
  }
  if (!order?.canUseDiscountCode) return null;
  return <>
      <Stack direction="row" sx={{
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%'
    }}>
        <Typography variant="body2" sx={{
        color: 'text.secondary'
      }}>
          <FormattedMessage id="cart.discountCode.label" defaultMessage={[{
          "type": 0,
          "value": "Discount code"
        }]} />
        </Typography>
        <Button data-testid="cart-enter-discount-code-button" variant="contained" color="monochrome" size="small" onClick={() => setDialogOpen(true)}>
          <FormattedMessage id="cart.discountCode.enterCode" defaultMessage={[{
          "type": 0,
          "value": "Enter code"
        }]} />
        </Button>
      </Stack>
      <DiscountCodeDialog open={dialogOpen} onClose={() => setDialogOpen(false)} encryptedOrderId={order.encryptedId} onApplied={() => showToast({
      title: intl.formatMessage({
        id: "cart.discountCode.appliedTitle",
        defaultMessage: [{
          "type": 0,
          "value": "Discount code applied"
        }]
      }),
      message: intl.formatMessage({
        id: "cart.discountCode.appliedMessage",
        defaultMessage: [{
          "type": 0,
          "value": "Your cart has been updated with the discount."
        }]
      }),
      severity: 'success'
    })} />
    </>;
};